import type { UUID } from "@/Shared/types";
import { BookShipmentLogisticsQuoteRow } from "@/Features/BookShipment/CustomComponents/BookShipmentLogisticsQuoteRow";
import QuotesRow from "@/Shared/Quotes/CustomComponents/QuotesRow";
import { QuotesSortOptionsType } from "@/Shared/Quotes/Types/QuotesSortOptionsType";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import _ from "lodash";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

interface ViewQuotesProps {
  shipmentId: UUID;
  pickupDate: moment.Moment;
  pickupDeadline: string | undefined;
  quotes: Readonly<Array<Quote>>;
  sortBy: QuotesSortOptionsType;
  onBook: (quoteId: UUID) => void;
  pickupLocation: PreQuoteLocation;
  deliveryLocation: PreQuoteLocation;
  deliveryDeadline: string | undefined;
  lineItems: Array<LineItem>;
  linearFeet: number | undefined;
  notes: string | undefined;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  equipmentType: EquipmentType | undefined;
  exclusiveUse: boolean;
  tarpRequired: boolean;
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
  onRequestManualQuotes: (
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) => Promise<void>;
}

function BookShipmentViewQuotes(props: ViewQuotesProps) {
  const {
    quotes,
    onBook,
    pickupDate,
    pickupLocation,
    deliveryLocation,
    deliveryDeadline,
    lineItems,
    linearFeet,
    notes,
    onRequestManualQuotes,
    sortBy,
    addInsuranceToShipment,
    insuranceAmount,
    insuranceCurrency,
    stateOfGoods,
    equipmentType,
    exclusiveUse,
    tarpRequired,
    pickupDeadline,
  } = props;

  if (!quotes) {
    throw new Error("Missing quotes");
  }

  if (quotes.length === 0) {
    return <></>;
  } else if (quotes.length === 1) {
    const onlyQuote = quotes[0];
    if (onlyQuote === undefined) {
      return <></>;
    }
    return (
      <>
        <QuotesRow
          pickupDeadline={pickupDeadline}
          pickupDate={pickupDate}
          pickupLocation={pickupLocation}
          deliveryLocation={deliveryLocation}
          lineItems={lineItems}
          key={onlyQuote.quoteId}
          quote={onlyQuote}
          onSelectQuote={() => {
            onBook(onlyQuote.quoteId);
          }}
          notes={notes}
          addInsuranceToShipment={addInsuranceToShipment}
          insuranceAmount={insuranceAmount}
          insuranceCurrency={insuranceCurrency}
          stateOfGoods={stateOfGoods}
          deliveryDeadline={deliveryDeadline}
          pickupReferenceNumber={props.pickupReferenceNumber}
          deliveryReferenceNumber={props.deliveryReferenceNumber}
          deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
        ></QuotesRow>
        <BookShipmentLogisticsQuoteRow
          pickupDeadline={pickupDeadline}
          shipmentId={props.shipmentId}
          pickupDate={pickupDate.toISOString()}
          pickupLocation={pickupLocation}
          deliveryLocation={deliveryLocation}
          lineItems={lineItems}
          onRequestManualQuotes={onRequestManualQuotes}
          equipmentType={equipmentType}
          exclusiveUse={exclusiveUse}
          linearFeet={linearFeet}
          tarpRequired={tarpRequired}
          deliveryDeadline={deliveryDeadline}
          pickupReferenceNumber={props.pickupReferenceNumber}
          deliveryReferenceNumber={props.deliveryReferenceNumber}
          deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
        />
      </>
    );
  } else {
    const firstQuote = quotes[0];
    if (firstQuote === undefined) {
      return <></>;
    }
    const otherQuotes = quotes.slice(1);

    // Show the logistics offer if we have fewer than 4 carriers returning quotes
    const uniqueCarrierCount = _.uniq(
      quotes.map((q) => q.carrierIdentifier),
    ).length;
    const noCheapQuotes = quotes.every(function (q) {
      return q.price > 3500;
    });

    const showLogistics = uniqueCarrierCount < 4 || noCheapQuotes;

    const sortByDescription =
      sortBy === QuotesSortOptionsType.SortByLowestPrice
        ? "Lowest Price"
        : "Fastest Quote";

    return (
      // A Stack, not a fragment: the screen renders this inside its own
      // `Stack gap={8}`, and a fragment's children are flattened into that
      // flex container — so all of the spacing below was landing on top of a
      // 32px gap around every heading, spacer and row. Its own rhythm is 32
      // above a section heading, 8 down to the first row, 16 between rows.
      <Stack gap={0}>
        <div className="h-[32px]" />
        <div className="flex w-full flex-col items-start">
          <h2 className="text-[18px] font-medium text-foreground">
            {sortByDescription}
          </h2>
        </div>
        <div className="h-[8px]" />
        <QuotesRow
          pickupDeadline={pickupDeadline}
          pickupDate={pickupDate}
          pickupLocation={pickupLocation}
          deliveryLocation={deliveryLocation}
          lineItems={lineItems}
          key={firstQuote.quoteId}
          quote={firstQuote}
          onSelectQuote={() => {
            onBook(firstQuote.quoteId);
          }}
          notes={notes}
          addInsuranceToShipment={addInsuranceToShipment}
          insuranceAmount={insuranceAmount}
          insuranceCurrency={insuranceCurrency}
          stateOfGoods={stateOfGoods}
          deliveryDeadline={deliveryDeadline}
          pickupReferenceNumber={props.pickupReferenceNumber}
          deliveryReferenceNumber={props.deliveryReferenceNumber}
          deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
        ></QuotesRow>

        <div className="h-[32px]" />
        <div className="flex w-full flex-col items-start">
          <h2 className="text-[18px] font-medium text-foreground">
            Other Quotes
          </h2>
        </div>
        <div className="h-[8px]" />

        {otherQuotes.map((quote: Quote) => {
          return (
            <QuotesRow
              pickupDeadline={pickupDeadline}
              pickupDate={pickupDate}
              pickupLocation={pickupLocation}
              deliveryLocation={deliveryLocation}
              lineItems={lineItems}
              key={quote.quoteId}
              quote={quote}
              onSelectQuote={() => {
                onBook(quote.quoteId);
              }}
              notes={notes}
              addInsuranceToShipment={addInsuranceToShipment}
              insuranceAmount={insuranceAmount}
              insuranceCurrency={insuranceCurrency}
              stateOfGoods={stateOfGoods}
              deliveryDeadline={deliveryDeadline}
              pickupReferenceNumber={props.pickupReferenceNumber}
              deliveryReferenceNumber={props.deliveryReferenceNumber}
              deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
              deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
            ></QuotesRow>
          );
        })}

        {showLogistics && (
          <BookShipmentLogisticsQuoteRow
            pickupDeadline={pickupDeadline}
            shipmentId={props.shipmentId}
            pickupDate={pickupDate.toISOString()}
            pickupLocation={pickupLocation}
            deliveryLocation={deliveryLocation}
            deliveryDeadline={deliveryDeadline}
            lineItems={lineItems}
            onRequestManualQuotes={onRequestManualQuotes}
            linearFeet={linearFeet}
            equipmentType={equipmentType}
            exclusiveUse={exclusiveUse}
            tarpRequired={tarpRequired}
            pickupReferenceNumber={props.pickupReferenceNumber}
            deliveryReferenceNumber={props.deliveryReferenceNumber}
            deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
            deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
          />
        )}
      </Stack>
    );
  }
}

export default BookShipmentViewQuotes;
